import { addDays, addMonths } from "date-fns";
import {
  type InstallmentOfferDetails,
  type CreditOffer,
  CreditProductType,
  type CreditLinePaymentExample,
  type CreditLineOfferDetails,
} from "../types/offer";
import { CREDIT_LINE_MIN_PAYMENT_PERCENTAGE } from "./useLoanOffer";
import type { ApplicationDraft } from "@/features/application/types";
import { defaultBillDate } from "@/lib/loans.utility";

/**
 * Max credit amount allowed for a given loan duration, per the offer's
 * periodCreditLimits ladder. Falls back to the offer-wide creditLimit.
 */
export const getMaxAmountForRates = (
  offer: CreditOffer,
  numberOfRates: number,
): number =>
  offer.periodCreditLimits?.find(
    (limit) => limit.loanDuration === numberOfRates,
  )?.creditLimit ?? offer.creditLimit;

/**
 * Smallest loan duration whose credit limit covers the requested amount.
 * Falls back to the offer's max number of rates.
 */
export const getMinRatesForAmount = (
  offer: CreditOffer,
  amount: number,
): number =>
  [...(offer.periodCreditLimits ?? [])]
    .sort((a, b) => a.loanDuration - b.loanDuration)
    .find((limit) => limit.creditLimit >= amount)?.loanDuration ??
  offer.maxNumberOfRates;

export const calculateCreditLinePaymentExample = (
  interestDailyPercentage: number,
  amount: number,
): CreditLinePaymentExample => {
  const interest = (amount * interestDailyPercentage) / 100;
  const monthlyCost = interest * 30;
  const minPayment = monthlyCost + amount * CREDIT_LINE_MIN_PAYMENT_PERCENTAGE;

  return {
    monthlyCostExample: monthlyCost,
    minPaymentExample: minPayment,
    interestExample: interest,
  };
};

export const calculateInstallmentLoanDetails = (
  offer: CreditOffer | undefined,
  amount: number | undefined,
  numberOfRates: number | undefined,
) => {
  if (!offer) {
    return undefined;
  }

  const selectedAmount = amount || offer?.creditLimit;
  const selectedNumberOfRates = numberOfRates || offer?.numberOfRates;
  const grantingCommission =
    offer.loanGrantingCommissionFixedAmount +
    (offer.loanGrantingCommissionPercentage * selectedAmount) / 100;
  const creditAmount = selectedAmount + grantingCommission;

  const monthlyInterestPercentage =
    offer?.interestDailyPercentage * offer?.paymentScheduleDuration;
  const rateInterest = monthlyInterestPercentage / 100.0;
  const annuity =
    rateInterest *
    (creditAmount / (1 - Math.pow(1 + rateInterest, -selectedNumberOfRates)));
  const creditPrice = annuity * selectedNumberOfRates - creditAmount;
  const totalAmount = creditAmount + creditPrice;
  const lastRateAmount = totalAmount - annuity * (selectedNumberOfRates - 1);

  const getRateSettlementDate = (rateIndex: number) => {
    const daysToAdd = rateIndex * (offer?.paymentScheduleDuration ?? 0) - 1;
    return addDays(new Date(), daysToAdd);
  };

  const calcRates =
    selectedNumberOfRates &&
    [...Array(selectedNumberOfRates).keys()].map((i) => {
      const rateNumber = i + 1;
      const isLastRate = rateNumber == selectedNumberOfRates;

      return {
        date: getRateSettlementDate(rateNumber),
        amount: isLastRate ? lastRateAmount : annuity,
      };
    });

  const lastRateDate =
    calcRates && calcRates.length > 0
      ? calcRates[calcRates.length - 1].date
      : undefined;

  const monthlyPayment =
    calcRates && calcRates.length > 0 ? calcRates[0].amount : undefined;

  return {
    selectedAmount,
    creditPrice: creditPrice,
    finalPaymentAmount: totalAmount,
    grantingCommission: grantingCommission,
    minAvailableNumberOfRates: getMinRatesForAmount(offer, selectedAmount),
    dae: offer?.effectiveAnnualPercentageRate,
    annualInterest: offer?.nominalAnnualPercentageRate,
    monthlyInterest: offer?.nominalMonthlyPercentageRate,
    interestDailyPercentage: offer?.interestDailyPercentage,
    penaltyDailyPercentage: offer?.penaltyInterestDailyPercentage,
    loanDuration: selectedNumberOfRates * offer?.paymentScheduleDuration,
    selectedNumberOfRates: selectedNumberOfRates,
    isAvailable: offer?.isAvailable,
    id: offer?.id,
    commission: offer?.commission,
    creditLimit: getMaxAmountForRates(offer, selectedNumberOfRates),
    rates: calcRates,
    lastRateDate: lastRateDate,
    creditAmount: creditAmount,
    monthlyPayment: monthlyPayment,
    isDerogation: isSelectedAmountDerogation(
      offer,
      selectedAmount,
      selectedNumberOfRates,
    ),
  } as InstallmentOfferDetails;
};

export function isSelectedAmountDerogation(
  offer: CreditOffer,
  requestedAmount: number,
  noOfRates: number,
): boolean {
  if (offer === null || offer === undefined || offer.salary < 1) {
    return false;
  }
  const maxBnrPercentage =
    offer.bnrLimitPercentage > 0 ? offer.bnrLimitPercentage + 0.1 : 40.01;
  return (
    calculateDegreeOfIndebtedness(offer, requestedAmount, noOfRates) >
    maxBnrPercentage
  );
}

export const calculateDegreeOfIndebtedness = (
  offer: CreditOffer,
  requestedAmount: number,
  noOfRates: number,
): number => {
  let degreeOfIndebtedness =
    offer.bnrLimitPercentage > 0 ? offer.bnrLimitPercentage : 40.0;

  if (offer.salary < 1) {
    return degreeOfIndebtedness;
  }

  let annuity: number;
  if (offer.type !== CreditProductType.CreditLine) {
    annuity = calculateAnnuity(
      requestedAmount,
      offer.interestDailyPercentage,
      offer.paymentScheduleDuration,
      noOfRates,
    );
  } else {
    annuity =
      requestedAmount * CREDIT_LINE_MIN_PAYMENT_PERCENTAGE +
      offer.costPerDay * offer.paymentScheduleDuration;
  }
  degreeOfIndebtedness =
    ((offer.monthlyPayments + annuity) / offer.salary) * 100.0;
  return degreeOfIndebtedness;
};

function calculateAnnuity(
  principal: number,
  dailyInterestPercentage: number,
  paymentScheduleDuration: number,
  numberOfRates: number,
): number {
  if (
    principal < 1 ||
    dailyInterestPercentage < 0.0001 ||
    paymentScheduleDuration < 1 ||
    numberOfRates < 1
  )
    return 0;
  const monthlyInterestPercentage =
    dailyInterestPercentage * paymentScheduleDuration;
  const rateInterest = monthlyInterestPercentage / 100;
  return (
    rateInterest *
    (principal / (1 - Math.pow(1 + rateInterest, -numberOfRates)))
  );
}

export const calculateCreditLineOfferDetails = (
  offer: CreditOffer | undefined,
  draft: ApplicationDraft,
  amount?: number
): CreditLineOfferDetails | undefined => {
  if (!offer || !draft) {
    return undefined;
  }

  const selectedAmount = amount ?? draft.amount ?? offer.creditLimit ?? 0;

  return {
    selectedAmount,
    firstBillDate: draft.firstBillDate ?? defaultBillDate(),
    finalDueDate: addMonths(new Date(), offer?.maxNumberOfRates ?? 0),
    isDerogation: isSelectedAmountDerogation(
      offer,
      selectedAmount,
      draft.numberOfRates ?? offer?.numberOfRates ?? 0
    ),
  };
};
